/**
 * Debug layout for instance sessions.
 *
 * Wraps @roj-ai/client's DebugShell with Buzola routing.
 * Each debug subpage is a Buzola route rendering the matching @roj-ai/client page component.
 *
 * Two modes:
 * - No sessionId in URL → renders overview (DebugDashboard via <Outlet />)
 * - sessionId in URL → renders DebugSessionLayout with DebugShell + DebugContext
 */
import { configureApiBaseUrl } from '@roj-ai/client'
import {
	BackIcon,
	DebugContext,
	type DebugContextValue,
	DebugShell,
	getNavItemClassName,
} from '@roj-ai/debug'
import type { SessionId } from '@roj-ai/shared'
import { Link, Outlet, useNavigate, useRoute } from '@buzola/router'
import { useCallback, useMemo } from 'react'

/** Merge params from all matched routes — layouts don't see child params via useParams(). */
function useAllParams(): Record<string, string | undefined> {
	const { matches } = useRoute()
	return useMemo(() => {
		const merged: Record<string, string | undefined> = {}
		for (const match of matches) {
			Object.assign(merged, match.params)
		}
		return merged
	}, [matches])
}

export default function DebugLayout() {
	const allParams = useAllParams()
	const { id, sessionId, organizationId } = allParams

	if (id) {
		configureApiBaseUrl(`/_admin/debug/${id}`)
	}

	if (!sessionId || !id || !organizationId) {
		return <Outlet />
	}

	return <DebugSessionLayout id={id} sessionId={sessionId} organizationId={organizationId} allParams={allParams} />
}

function DebugSessionLayout({ id, sessionId, organizationId, allParams }: {
	id: string
	sessionId: string
	organizationId: string
	allParams: Record<string, string | undefined>
}) {
	const buzolaNavigate = useNavigate()

	configureApiBaseUrl(`/_admin/debug/${id}`)

	const navigate = useCallback((subpath: string) => {
		const parts = subpath.split('/')
		const base = parts[0]

		const baseParams = { organizationId, id, sessionId }
		if (base === 'agents' && parts[1]) {
			buzolaNavigate('app/org/instances/detail/debug/agents-detail', { params: { ...baseParams, agentId: parts[1] } })
		} else if (base === 'llm-calls' && parts[1]) {
			buzolaNavigate('app/org/instances/detail/debug/llm-call', { params: { ...baseParams, callId: parts[1] } })
		} else {
			buzolaNavigate(`app/org/instances/detail/debug/${base}` as 'app/org/instances/detail/debug/dashboard', { params: baseParams })
		}
	}, [buzolaNavigate, organizationId, id, sessionId])

	const createHref = useCallback((subpath: string) => {
		return `/app/org/${organizationId}/instances/${id}/debug/${sessionId}/${subpath}`
	}, [organizationId, id, sessionId])

	const isActive = useCallback((subpath: string) => {
		const current = window.location.pathname
		const target = `/app/org/${organizationId}/instances/${id}/debug/${sessionId}/${subpath}`
		return current === target || current.startsWith(target + '/')
	}, [organizationId, id, sessionId])

	const ctx = useMemo((): DebugContextValue => ({
		sessionId: sessionId as SessionId,
		params: allParams,
		navigate,
		createHref,
		isActive,
	}), [sessionId, allParams, navigate, createHref, isActive])

	return (
		<DebugContext.Provider value={ctx}>
			<DebugShell
				sessionId={sessionId}
				className="flex flex-col h-[calc(100vh-8rem)]"
				renderNavItem={(item) => (
					<button
						key={item.to}
						onClick={() => navigate(item.to)}
						className={getNavItemClassName(isActive(item.to))}
					>
						<item.icon />
						{item.label}
					</button>
				)}
				sidebarFooter={
					<Link
						to="app/org/instances/detail/debug"
						params={{ id }}
						className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-2xl transition-colors"
					>
						<BackIcon />
						Back to Overview
					</Link>
				}
			>
				<Outlet />
			</DebugShell>
		</DebugContext.Provider>
	)
}
